import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { useAuth } from "../../../auth";

const MarkSessionDialog = ({
  open,
  onClose,
  appointmentID,
  clientID,
  onSubmit,
  hideSubmitButton = false,
}) => {
  const [sessionStatus, setSessionStatus] = useState("Completed");
  const [cancellationReason, setCancellationReason] = useState("");
  const [note, setNote] = useState("");
  const [cancellationReasons, setCancellationReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [fetchedCancellationReason, setFetchedCancellationReason] = useState("");
  const [loadingFeedbackDetails, setLoadingFeedbackDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const { currentUser } = useAuth();

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSessionStatus("Completed");
      setCancellationReason("");
      setNote("");
      setCancellationReasons([]);
      setFetchedCancellationReason("");
      setSubmitting(false);
      setSnackbarOpen(false);
    }
  }, [open]);

  // Fetch feedback details when hideSubmitButton is true
  useEffect(() => {
    if (open && hideSubmitButton && appointmentID) {
      handleViewSessionNotes();
    }
  }, [open, hideSubmitButton, appointmentID]);

  // Fetch cancellation reasons from API
  const fetchCancellationReasons = useCallback(async () => {
    setLoadingReasons(true);
    try {
      const authToken = localStorage.getItem("Authorization")?.split("Bearer ")[1] || 
                        sessionStorage.getItem("token");
      
      if (!authToken) {
        console.error("No authentication token found");
        setLoadingReasons(false);
        return;
      }

      const response = await fetch(
        "https://odoo.unitedwecare.ca/new-uwc/appointment/session-feedback-form",
        {
          method: "POST",
          headers: {
            "accept": "*/*",
            "accept-language": "en-US,en;q=0.9,te;q=0.8",
            "authorization": `Bearer ${authToken}`,
            "content-type": "application/json",
            "deviceid": "123",
            "devicetype": "web",
            "lat": "1",
            "long": "1",
            "origin": "https://my.unitedwecare.com",
            "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
            "x-email-id": currentUser?.Emailid || "",
          },
          body: JSON.stringify({
            user_type: "expert",
            user_id: currentUser?.UserId || 0,
            session_status: "incompleted",
            country: currentUser?.Country || "Australia",
          }),
        }
      );

      const data = await response.json();
      
      if (data?.ResponseCode === 200 && data?.data?.template_data?.[0]?.options) {
        const options = data.data.template_data[0].options.map((option) => ({
          id: option.option_id,
          name: option.name,
          value: option.value,
        }));
        setCancellationReasons(options);
      } else {
        console.error("Failed to fetch cancellation reasons:", data);
        // Fallback to empty array or default reasons
        setCancellationReasons([]);
      }
    } catch (error) {
      console.error("Error fetching cancellation reasons:", error);
      setCancellationReasons([]);
    } finally {
      setLoadingReasons(false);
    }
  }, [currentUser]);

  // Fetch cancellation reasons when dialog opens and status is "Not Completed"
  useEffect(() => {
    if (open && sessionStatus === "Not Completed") {
      fetchCancellationReasons();
    }
  }, [open, sessionStatus]);

  const handleClose = () => {
    setSessionStatus("Completed");
    setCancellationReason("");
    setNote("");
    onClose();
  };

  const handleViewSessionNotes = useCallback(async () => {
    setLoadingFeedbackDetails(true);
    try {
      const authToken = localStorage.getItem("Authorization")?.split("Bearer ")[1] || 
                        sessionStorage.getItem("token");
      
      if (!authToken) {
        console.error("No authentication token found");
        setLoadingFeedbackDetails(false);
        return;
      }

      const response = await fetch(
        "https://odoo.unitedwecare.ca/new-uwc/appointment/expert-feedback-details",
        {
          method: "POST",
          headers: {
            "accept": "*/*",
            "accept-language": "en-US,en;q=0.9,te;q=0.8",
            "authorization": `Bearer ${authToken}`,
            "content-type": "application/json",
            "deviceid": "123",
            "devicetype": "web",
            "lat": "1",
            "long": "1",
            "origin": "https://my.unitedwecare.com",
            "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
            "x-email-id": currentUser?.Emailid || "",
          },
          body: JSON.stringify({
            user_id: currentUser?.UserId || 0,
            appointment_id: appointmentID,
            user_type: "expert",
          }),
        }
      );

      const data = await response.json();
      
      if (data?.ResponseCode === 200 && data?.data?.[0]?.qa_list?.[0]) {
        const qaItem = data.data[0].qa_list[0];
        // If type is "Other", use answer_value, otherwise use answer
        const reason = qaItem.type === "Other" 
          ? qaItem.answer_value || qaItem.answer 
          : qaItem.answer;
        setFetchedCancellationReason(reason || "");
      } else {
        console.error("Failed to fetch feedback details:", data);
        setFetchedCancellationReason("");
      }
    } catch (error) {
      console.error("Error fetching feedback details:", error);
      setFetchedCancellationReason("");
    } finally {
      setLoadingFeedbackDetails(false);
    }
  }, [currentUser, appointmentID]);

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      if (onSubmit) {
        // If custom onSubmit handler is provided, use it
        await onSubmit({
          appointmentID,
          clientID,
          status: sessionStatus,
          cancellationReason: sessionStatus === "Not Completed" ? cancellationReason : null,
          note: note || null,
        });
        setSnackbarMessage("Session marked successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        handleClose();
      } else {
        // Default API call behavior
        let noteSuccess = true;
        let feedbackSuccess = true;
        
        if(note != ""){
          try {
            const payload = {
              appointment_id: appointmentID?.toString(),
              patient_id: clientID?.toString(),
              expert_id: currentUser?.UserId?.toString(),
              comment: note || "",
            };
    
            console.log("payload", payload);
            const response = await fetch(
              "https://us.uwc.world/new-uwc/expert/dashboard/add-expert-comment",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
                },
                body: JSON.stringify(payload),
              }
            );
            const postResponse = await response.json();
            console.log("postResponse", postResponse);
            
            if (!response.ok || (postResponse?.ResponseCode && postResponse.ResponseCode !== 200)) {
              noteSuccess = false;
            }
          } catch (error) {
            console.error("Error:", error);
            noteSuccess = false;
          }
        }

        try{
          // Build qa_list based on session status
          let qaList = [];
          
          if (sessionStatus === "Not Completed" && cancellationReason) {
            // Find the selected cancellation reason
            const selectedReason = cancellationReasons.find(
              (reason) => reason.value === cancellationReason
            );
            
            if (selectedReason) {
              qaList = [
                {
                  question_id: selectedReason.id,
                  answer: selectedReason.name,
                }
              ];
            }
          }
          // If "Completed", qa_list remains empty array
          
          const payload = {
            user_type: "expert",
            user_id: currentUser?.UserId?.toString(),
            appointment_id: appointmentID?.toString(),
            session_status: sessionStatus === "Not Completed" ? "incompleted" : "completed",
            qa_list: qaList,
          };
          const response = await fetch(
            "https://odoo.unitedwecare.ca/new-uwc/appointment/feedback-form-data",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
              },
              body: JSON.stringify(payload),
            }
          );
          const postResponse = await response.json();
          console.log("postResponse", postResponse);
          
          if (!response.ok || (postResponse?.ResponseCode && postResponse.ResponseCode !== 200)) {
            feedbackSuccess = false;
          }
        } catch (error) {
          console.error("Error:", error);
          feedbackSuccess = false;
        }

        console.log("noteSuccess", noteSuccess);
        console.log("feedbackSuccess", feedbackSuccess);
        
        // Determine overall success: both operations must succeed
        // If note is empty, only check feedbackSuccess
        // If note is provided, both must succeed
        const overallSuccess = note !== "" 
          ? (noteSuccess && feedbackSuccess)
          : feedbackSuccess;
        
        // Show appropriate snackbar message
        if (overallSuccess) {
          setSnackbarMessage("Session marked successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          // Close dialog on success after a short delay to show snackbar
          setTimeout(() => {
            handleClose();
          }, 3000);
        } else {
          setSnackbarMessage("Failed to mark session. Please try again.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
          // Keep dialog open on error so user can see the message and try again
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle 
        sx={{ 
          pb: 1.5, 
          fontWeight: 600,
          position: "relative",
          pr: hideSubmitButton ? 6 : 3,
        }}
      >
        {hideSubmitButton ? "View Session Reason" : "Mark Session"}
        {hideSubmitButton && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
          {/* Status Selection */}
          {!hideSubmitButton && (
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 1.5, fontWeight: 500 }}>
              Select Status
            </FormLabel>
            <RadioGroup
              value={sessionStatus}
              onChange={(e) => setSessionStatus(e.target.value)}
              row
            >
              <FormControlLabel
                value="Completed"
                control={<Radio />}
                label="Completed"
              />
              <FormControlLabel
                value="Not Completed"
                control={<Radio />}
                label="Not Completed"
              />
            </RadioGroup>
          </FormControl>
          )}
          {/* Cancellation Reason Dropdown (only if Not Completed) */}
          {sessionStatus === "Not Completed" && (
            <FormControl fullWidth>
              <InputLabel id="cancellation-reason-label">
                Cancellation Reason
              </InputLabel>
              <Select
                labelId="cancellation-reason-label"
                value={cancellationReason}
                label="Cancellation Reason"
                onChange={(e) => setCancellationReason(e.target.value)}
                disabled={loadingReasons}
              >
                {loadingReasons ? (
                  <MenuItem value="">Loading...</MenuItem>
                ) : cancellationReasons.length > 0 ? (
                  cancellationReasons.map((reason) => (
                    <MenuItem key={reason.id} value={reason.value}>
                      {reason.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">No reasons available</MenuItem>
                )}
              </Select>
            </FormControl>
          )}

          {hideSubmitButton && (
            <TextField
              label="Cancellation Reason"
              value={loadingFeedbackDetails ? "Loading..." : fetchedCancellationReason || "No cancellation reason available"}
              fullWidth
              disabled
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#000000",
                },
              }}
            />
          )}

          {/* Optional Note */}
          {!hideSubmitButton && (
            <TextField
            label="Note (Optional)"
            multiline
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add any additional notes..."
            fullWidth
          />
          )}
        </Box>
      </DialogContent>
      {!hideSubmitButton && (
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={handleClose}
            sx={{
              textTransform: "capitalize",
              color: "#6B7280",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={(sessionStatus === "Not Completed" && !cancellationReason) || submitting}
            sx={{
              textTransform: "capitalize",
              bgcolor: "var(--primary-base-color)",
              "&:hover": {
                bgcolor: "var(--primary-base-color-darken)",
              },
            }}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default MarkSessionDialog;

